import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// REDUX
import { useSelector } from "react-redux";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionCommandButton from "../Actions/components/ActionCommandButton";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
// CONSTANTS
import {
  resolveCommandEnum,
  resolveValidStateEnums,
  assertBoundCommand,
  UNBOUND_COMMAND_REASON,
  dispatchFloor,
  floorSelectionMissing,
} from "./actionButtonUtils";
import useAwaitingTargetReason from "./useAwaitingTargetReason";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
// VERIFICATION
import useActionOutcome, { ActionVerification } from "./useActionOutcome";

/**
 * Whether this type releases the bit itself, and after how long.
 *
 * DEFAULT: it does not. One press sends `active: true` once, and the controller
 * owns the state from there — which is what registering a call actually is.
 *
 * The release is opt-in through the row's `timer`, because whether a release is
 * even wanted is a property of the CONTROLLER, not of this button, and it is
 * not something the dashboard can discover:
 *
 *   - An edge-triggered controller latches on the transition and expects the
 *     master to let go. Holding the bit is untidy but harmless.
 *   - A level-triggered controller treats the asserted bit AS the request.
 *     Releasing it cancels the call the operator just placed.
 *
 * The two mistakes are not equally bad. Holding a bit the controller no longer
 * needs costs nothing an operator can see; dropping a call they just registered
 * is a fault they will report as "the button does not work". So the default is
 * to send once and leave it, and a controller that needs a pulse gets one by
 * setting `timer` on the row.
 *
 * An earlier version of this component always released after three seconds and
 * showed a "clears in 3… 2… 1" countdown. Both were wrong. The countdown
 * presented a release the UI had scheduled as if it were knowledge of the
 * controller's progress, and the release itself assumed an edge-triggered
 * controller that was never verified.
 */
export const DEFAULT_ONE_SHOT_RELEASES = false;

/**
 * One button that sends a SINGLE bound command once and hands the state to the
 * controller.
 *
 * This is what registering a hall or car call is: the master asserts the
 * request, the controller latches it, and the call ends when the car arrives.
 * The operator's involvement is the press — there is nothing further for them
 * to do, and nothing for the UI to predict.
 *
 * No other type here can express that. TURNBACK, SWITCH and
 * ENERGIZER_TURN_BACK all "finish" by dispatching a DIFFERENT command, so a
 * control whose only other edge is the same bit released had no type at all,
 * and auto-seeded call registrations were bound to ON_OFF: an on button that
 * latched a call indefinitely, beside an off button whose only effect was to
 * deassert a bit the controller had already moved past.
 *
 * The button is briefly disabled while the command is in flight, so a double
 * press cannot put two identical registrations on the wire. It does NOT show a
 * countdown: the only clock the UI has is its own, and the thing an operator
 * would read a countdown as measuring — when the call completes — is not on it.
 * The call's real state reaches the dashboard the way every other controller
 * state does, through the traffic indicator for that floor.
 */
export default function ActionButtonOneShot({
  validStates,
  label,
  messageId,
  command = null,
  colorAction,
  carService,
  timerAction = 0,
  targetFloor = undefined,
  registerSend = undefined,
  expectedResponse = null,
  expectedResponseTimeout = null,
  commandMessageId = null,
}: {
  validStates: Array<string | UuidT>;
  label: string;
  messageId: string | UuidT;
  command?: ActionCommandOption | null;
  colorAction: string;
  carService: number;
  /** Hold, in seconds. 0 or unset falls back to the default above. */
  timerAction?: number;
  /**
   * The floor this command dispatches to. `undefined` for a command that takes
   * no floor, `null` while a floor-taking one waits on the operator's choice.
   */
  targetFloor?: number | null;
  /**
   * Register this button's send so a floor tap can invoke it.
   *
   * The floor is passed IN to the registered function rather than read from
   * `targetFloor`, and that is the point: the parent calls it in the same tick
   * it selects the floor, before its own state has updated. Reading
   * `targetFloor` there would dispatch at the PREVIOUS floor — a command sent
   * where the operator did not ask, which is precisely what the two-step
   * interaction exists to prevent.
   *
   * Registration is idempotent, so React StrictMode's double-invoke in
   * development re-registers the same wrapper rather than sending twice.
   */
  registerSend?: (send: (floorOverride?: number) => void) => void;
} & ActionVerification) {
  const [t] = useTranslation("global");

  // Whether the bit is currently held asserted. Deliberately a boolean and not
  // a countdown: the UI knows how long IT will hold the bit, and nothing about
  // how long the controller will take, so there is no number here honest enough
  // to show an operator.
  const [pulsing, setPulsing] = useState(false);

  const { theme } = useSelector((state: RootState) => state.home);

  const { triggerPostCommand } = usePostCommand();
  const { messages } = useGetAllMessages();
  const reportOutcome = useActionOutcome({
    expectedResponse,
    expectedResponseTimeout,
    commandMessageId,
  });

  // Held in a ref, not state, so the cleanup below can cancel it without the
  // effect re-running and rescheduling what it just cancelled.
  const releaseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards the deassert against a component that has gone away mid-pulse: an
  // operator can close the drawer between the press and the clear, and firing
  // a command from an unmounted component would post a request nothing is
  // listening for and warn on the state update.
  const mountedRef = useRef(true);

  // null (not 0) when unbound — Control enum 0 is CarCallLock, a real command,
  // so a 0 fallback would silently fire it.
  const messageEnum = useMemo(
    () => resolveCommandEnum(messages, messageId, command),
    [messages, messageId, command],
  );

  const validStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, validStates),
    [messages, validStates],
  );

  // A row's `timer` is in SECONDS and opts this row into releasing the bit that
  // long after the press. Unset (the seeded default) means send once and leave
  // the controller to it.
  const releaseAfterMs = timerAction && timerAction > 0 ? timerAction * 1000 : null;

  const blockedByService = validStatesNumbers.includes(carService);
  const awaitingFloor = floorSelectionMissing(targetFloor);
  const selectFloorReason = useAwaitingTargetReason(messageEnum);

  const stopTimers = useCallback(() => {
    if (releaseRef.current !== null) {
      clearTimeout(releaseRef.current);
      releaseRef.current = null;
    }
  }, []);

  // Cancel on unmount. Without this the pending clear fires into a dead
  // component; with it, a drawer closed mid-pulse simply leaves the bit set,
  // which is the honest outcome — the UI cannot claim to have cleared
  // something it never sent.
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      stopTimers();
    };
  }, [stopTimers]);

  // A ref so the registered wrapper stays stable while always calling the
  // CURRENT handler — re-registering on every render would churn the parent's
  // ref for no benefit.
  const sendRef = useRef<(floorOverride?: number) => void>(() => {});

  useEffect(() => {
    registerSend?.((floorOverride?: number) => sendRef.current(floorOverride));
  }, [registerSend]);

  const sendCommand = async (floorOverride?: number) => {
    if (!assertBoundCommand(messageEnum, label, theme)) {
      reportOutcome(label, false, messageId);
      return;
    }

    // Show the pulse before awaiting, so a slow POST cannot leave the button
    // looking idle and invite a second press.
    setPulsing(true);

    // Resolved once, here, and reused by the timed release below. The release
    // fires seconds later, by which time targetFloor may name a different
    // floor — deasserting one call by clearing another is a fault nobody would
    // trace back to this button.
    const floorForThisPress = dispatchFloor(floorOverride ?? targetFloor);

    const delivered = await triggerPostCommand({
      floor: floorForThisPress,
      command: messageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });

    reportOutcome(label, delivered, messageId);

    // Nothing was asserted, so there is nothing to clear. Scheduling the
    // deassert anyway would send an `active: false` for a call that was never
    // registered — harmless on this controller, misleading in a trace.
    if (!delivered) {
      setPulsing(false);
      return;
    }

    if (!mountedRef.current) return;

    // No release configured: the command is sent and the interaction is over.
    // The call's state from here is the controller's to report, and the
    // dashboard already renders that through this floor's traffic indicator.
    if (releaseAfterMs === null) {
      setPulsing(false);
      return;
    }

    releaseRef.current = setTimeout(() => {
      stopTimers();
      if (!mountedRef.current) return;
      setPulsing(false);

      // Deliberately not awaited and its result not reported: the operator was
      // already told the press landed, and a failed auto-clear is the
      // controller's business — it clears the call on arrival regardless. A
      // second outcome toast here would report a "failure" for a call that
      // completed normally.
      triggerPostCommand({
        floor: floorForThisPress,
        command: messageEnum,
        liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
        active: false,
      }).catch(() => {});
    }, releaseAfterMs);
  };

  /**
   * The button's own onClick, which deliberately takes NO arguments.
   *
   * `sendCommand` is not wired to onClick directly: React would pass the click
   * EVENT as its first argument, that event would be read as the floor
   * override, and the command would go out with a SyntheticEvent where a floor
   * index belongs. Nothing throws — the button looks like it worked.
   */
  const onClickCommand = () => {
    // Result deliberately unused: sendCommand reports its own outcome through
    // the toast, and there is no caller here to hand a rejection to.
    sendCommand().catch(() => {});
  };

  sendRef.current = sendCommand;

  // A function rather than stacked ternaries: an unbound command outranks the
  // in-flight state, since it is the one a press can never clear.
  const disabledReason = (): string | undefined => {
    if (messageEnum == null) return UNBOUND_COMMAND_REASON;
    if (awaitingFloor) return selectFloorReason;
    if (pulsing) return t("monitoringActions.pulseInFlight", "Sending…");
    return undefined;
  };

  return (
    <ActionCommandButton
      label={label}
      color={colorAction}
      outlined
      // A spinner for the length of the pulse, never a countdown. The UI knows
      // only how long IT holds the bit; when the CALL clears is the controller's
      // to report, and the dashboard already shows that through the traffic
      // indicator for this floor.
      loading={pulsing}
      disabled={messageEnum == null || blockedByService || awaitingFloor || pulsing}
      disabledReason={disabledReason()}
      onClick={onClickCommand}
    />
  );
}
